/**
 * Antigravity Scroll Animations
 * Premium IntersectionObserver-based reveal system.
 * Respects prefers-reduced-motion. Zero dependencies.
 * Disabled on mobile view (scroll animations run only on desktop and tablet).
 */
(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMedia = window.matchMedia('(max-width: 767px)');

  function isMotionDisabled() {
    return prefersReduced.matches || mobileMedia.matches;
  }

  // Elements to animate and their variants
  const TARGETS = [
    { selector: '.card',                   variant: 'fade-up',    stagger: true,  base: 60 },
    { selector: '.collection-tile',        variant: 'fade-up',    stagger: true,  base: 80 },
    { selector: '.plp-hero-copy',          variant: 'fade-left',  stagger: false, base: 0  },
    { selector: '.plp-hero-img',           variant: 'fade-right', stagger: false, base: 0  },
    { selector: '.plp-about',              variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.collections-list-header', variant: 'fade-up',  stagger: false, base: 0  },
    { selector: '.step',                   variant: 'fade-up',    stagger: true,  base: 80 },
    { selector: '.promise-item',           variant: 'fade-up',    stagger: true,  base: 60 },
    { selector: '.tile',                   variant: 'fade-up',    stagger: true,  base: 80 },
    { selector: '.portrait',               variant: 'scale-in',   stagger: false, base: 0  },
    { selector: '.timothy-copy',           variant: 'fade-left',  stagger: false, base: 0  },
    { selector: '.footer-about',           variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.search-col-tile',        variant: 'fade-up',    stagger: true,  base: 60 },
    { selector: '.quote',                  variant: 'fade-up',    stagger: true,  base: 60 },
    { selector: '.promo',                  variant: 'scale-in',   stagger: false, base: 0  },
    { selector: '.collection-description-bottom', variant: 'fade-up', stagger: false, base: 0 },
    { selector: '.band#wholesale',         variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.access',                 variant: 'fade-up',    stagger: false, base: 0  },
    // Additional animations for enhanced premium feel
    { selector: '.crumbs',                 variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.crumbs li',              variant: 'fade-up',    stagger: true,  base: 40 },
    { selector: '.card h3',                variant: 'fade-up',    stagger: true,  base: 50 },
    { selector: '.price',                  variant: 'fade-up',    stagger: true,  base: 50 },
    { selector: '.rating',                 variant: 'fade-up',    stagger: true,  base: 40 },
    { selector: '.wishlist',               variant: 'fade-in',    stagger: true,  base: 30 },
    { selector: '.notify-me-form',         variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.cart-drawer',            variant: 'fade-in-right', stagger: false, base: 0  },
    // New pages & sections (How It's Made, Contact, Wholesale, etc.)
    { selector: '.mk-step',                variant: 'fade-up',    stagger: true,  base: 80 },
    { selector: '.mk-cta',                 variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.point',                  variant: 'fade-up',    stagger: true,  base: 70 },
    { selector: '.points-head',            variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.way',                    variant: 'fade-up',    stagger: true,  base: 60 },
    { selector: '.qa-item',                variant: 'fade-up',    stagger: true,  base: 50 },
    { selector: '.form-sec .form-intro',   variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.form-sec .tdc',          variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.steps-mini li',          variant: 'fade-up',    stagger: true,  base: 40 },
    { selector: '.band#halloween .band-img', variant: 'fade-right', stagger: false, base: 0 },
    { selector: '.band#halloween .band-copy', variant: 'fade-left', stagger: false, base: 0 },
    { selector: '.st-hero-media',          variant: 'scale-in',   stagger: false, base: 0  },
    { selector: '.st-intro',               variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.st-design',              variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.st-gallery-grid figure', variant: 'scale-in',   stagger: true,  base: 60 },
    { selector: '.tl-item',                variant: 'fade-up',    stagger: true,  base: 60 },
    { selector: '.visit-card',             variant: 'fade-up',    stagger: true,  base: 70 },
    { selector: '.st-quote blockquote',    variant: 'fade-up',    stagger: false, base: 0  },
    // Blog & Article pages
    { selector: '.article-hero',           variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.article-meta',           variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.blog-list-title',        variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.blog-list-sub',          variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.blog-list-page .card',   variant: 'fade-up',    stagger: true,  base: 70 },
    // Story / About page sections
    { selector: '.story-band',             variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.story-copy',             variant: 'fade-left',  stagger: false, base: 0  },
    { selector: '.story-img',              variant: 'fade-right', stagger: false, base: 0  },
    { selector: '.st-press',               variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.press li',               variant: 'fade-up',    stagger: true,  base: 50 },
    { selector: '.st-timeline',            variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.tl li',                  variant: 'fade-up',    stagger: true,  base: 60 },
    { selector: '.st-visit',               variant: 'fade-up',    stagger: false, base: 0  },
    // Section headings (homepage + inner pages)
    { selector: '.section-head',           variant: 'fade-up',    stagger: false, base: 0  },
    // Product page
    { selector: '.product-media',          variant: 'fade-left',  stagger: false, base: 0  },
    { selector: '.product-info',           variant: 'fade-right', stagger: false, base: 0  },
    { selector: '.product-desc',           variant: 'fade-up',    stagger: false, base: 0  },
    { selector: '.product-meta',           variant: 'fade-up',    stagger: false, base: 0  },
    // Newsletter / Access section copy
    { selector: '.access-copy',            variant: 'fade-right', stagger: false, base: 0  },
    { selector: '.access-img',             variant: 'fade-left',  stagger: false, base: 0  },
    // Promise strip items
    { selector: '.promise li',             variant: 'fade-up',    stagger: true,  base: 50 },
    // Related products & product flock headings
    { selector: '.related-products .section-head', variant: 'fade-up', stagger: false, base: 0 },
  ];

  function tagElements() {
    TARGETS.forEach(({ selector, variant, stagger, base }) => {
      document.querySelectorAll(selector).forEach((el, i) => {
        // Skip if already tagged
        if (el.hasAttribute('data-anim')) return;
        el.setAttribute('data-anim', variant);
        if (stagger) {
          const delay = base * (i % 4); // max 4 stagger columns
          el.style.setProperty('--anim-delay', `${delay}ms`);
        }
      });
    });
  }

  function markAllVisible() {
    document.querySelectorAll('[data-anim]').forEach(el => {
      el.classList.add('anim-visible');
    });
  }

  function buildObserver() {
    return new IntersectionObserver((entries, currentObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          requestAnimationFrame(() => {
            el.classList.add('anim-visible');
          });
          currentObserver.unobserve(el);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });
  }

  let observer;

  function init() {
    tagElements();

    if (isMotionDisabled()) {
      markAllVisible();
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      return;
    }

    if (!('IntersectionObserver' in window)) {
      markAllVisible();
      return;
    }

    if (!observer) {
      observer = buildObserver();
    }

    document.querySelectorAll('[data-anim]:not(.anim-visible)').forEach(el => {
      observer.observe(el);
    });
  }

  function handleMediaChange() {
    if (isMotionDisabled()) {
      markAllVisible();
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    } else {
      init();
    }
  }

  if (mobileMedia.addEventListener) {
    mobileMedia.addEventListener('change', handleMediaChange);
    prefersReduced.addEventListener('change', handleMediaChange);
  } else if (mobileMedia.addListener) {
    mobileMedia.addListener(handleMediaChange);
    prefersReduced.addListener(handleMediaChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-tag after AJAX updates (Load More, filter changes)
  window.addEventListener('tdcAnimRefresh', () => {
    tagElements();
    if (isMotionDisabled()) {
      markAllVisible();
    } else if (observer) {
      document.querySelectorAll('[data-anim]:not(.anim-visible)').forEach(el => {
        observer.observe(el);
      });
    }
  });

  // Expose refresh function for AJAX-loaded content
  window.tdcRefreshAnimations = () => {
    window.dispatchEvent(new CustomEvent('tdcAnimRefresh'));
  };
})();
